import React from 'react';
import { Button } from 'antd';
import StatusEyebrow from './StatusEyebrow';
import FaxCard from './FaxCard';
import { IconUsers } from './icons';
import type { TSubmitScreen } from '../types';
import s from '../s.module.less';

type Props = {
    isGroupSubmit: boolean;
    providerCount: number;
    slaDays: number;
    // Account 轴：已经是 Klarity 用户的人不再被推销，整张 offer 卡与页脚切换链都不渲染
    isExistingUser: boolean;
    faxMissing: boolean;
    // group offer 卡的三态由已邀请人数决定
    invitedCount: number;
    inviteTotal: number;
    onTrack: () => void;
    onGo: (screen: TSubmitScreen) => void;
    // lead form 判出还差一条 onboarding 时才有值，否则不渲染这个按钮
    continueLabel?: string;
    onContinueOnboarding: () => void;
};

export default function HomeScreen({
    isGroupSubmit,
    providerCount,
    slaDays,
    isExistingUser,
    faxMissing,
    invitedCount,
    inviteTotal,
    onTrack,
    onGo,
    continueLabel,
    onContinueOnboarding,
}: Props) {
    const heading = isGroupSubmit
        ? `We've received all ${providerCount} credentialing applications.`
        : 'Your credentialing application is in.';
    const subText = isGroupSubmit
        ? "We'll review each provider's information and notify you if anything is needed."
        : "We'll review your information and notify you if anything is needed.";
    const trackLabel = isGroupSubmit ? `Track all ${providerCount} applications` : 'Track my application';

    // 全员已邀请 / 部分已邀请 / 未邀请：只有 group 有这层状态
    const allInvited = isGroupSubmit && inviteTotal > 0 && invitedCount === inviteTotal;
    const someInvited = isGroupSubmit && invitedCount > 0 && !allInvited;

    let offerTitle = isGroupSubmit ? 'Help more patients find your practice.' : 'Help new patients find you.';
    let offerLead = isGroupSubmit
        ? 'Choose which providers to list for cash-pay visits while credentialing is underway.'
        : 'Prepare your profile for cash-pay visits while credentialing is underway.';
    let primaryLabel = isGroupSubmit ? 'Choose providers to list' : 'Preview my listing';
    if (someInvited) {
        offerLead = `${invitedCount} of ${inviteTotal} providers are invited. Invite the rest whenever they are ready.`;
        primaryLabel = 'Invite more providers';
    }
    if (allInvited) {
        offerTitle = 'Your team is invited.';
        offerLead = `All ${inviteTotal} providers have an invite to set up their Klarity login and listing. Each one sets their own rates and availability.`;
    }

    return (
        <>
            {/* 确认区：状态而非庆祝——左对齐的 Received by Klarity 取代了原来的居中 cheers 图 */}
            <div className={s.confirm}>
                <StatusEyebrow label="Received by Klarity" />
                <h1 className={s.h1}>{heading}</h1>
                <p className={s.lead}>{subText}</p>
                <div className={s.trackRow}>
                    <Button type="link" onClick={onTrack}>
                        {trackLabel} &rarr;
                    </Button>
                </div>
                {continueLabel && (
                    <Button size="large" className={s.continueBtn} onClick={onContinueOnboarding}>
                        {continueLabel}
                    </Button>
                )}
            </div>

            {/* 时效承诺收进折叠：它是安心话不是动作，而且天数被当成批准日期读过，所以和「这不是批准日期」放在一起 */}
            <details className={s.timing}>
                <summary>What happens next?</summary>
                <p>
                    Our team reviews {isGroupSubmit ? "each provider's information" : 'your information'}, then prepares
                    your payer applications. We&apos;ll notify you if anything needs attention. Our target is to start
                    payer submissions within <b>{slaDays} days</b> of receiving your documents. This is not a payer
                    approval date.
                </p>
            </details>

            {/* 唯一的可选 offer：无 dismiss（这页只看一次，关掉不改变下次），退出口是上面的 Track */}
            {!isExistingUser && (
                <section className={s.card}>
                    <div className={s.eyebrow}>
                        <IconUsers />
                        {allInvited ? 'Klarity Marketplace' : 'Optional next step · Klarity Marketplace'}
                    </div>
                    <h2 className={s.offerTitle}>{offerTitle}</h2>
                    <p className={s.lead}>{offerLead}</p>
                    {!allInvited && (
                        <div className={s.benefits}>
                            <span>
                                <span className={s.benefitCheck}>&#10003;</span>You set your rates
                            </span>
                            <span>
                                <span className={s.benefitCheck}>&#10003;</span>You control availability
                            </span>
                        </div>
                    )}
                    <div className={s.actions}>
                        {allInvited ? (
                            <Button type="link" onClick={() => onGo('choose')}>
                                View invited providers
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                               
                                onClick={() => onGo(isGroupSubmit ? 'choose' : 'preview')}
                            >
                                {primaryLabel}
                            </Button>
                        )}
                    </div>
                </section>
            )}

            {faxMissing && <FaxCard />}

            {/* 页脚切换：另一个 offer 只做安静的链接，右边说明这张卡不是一次性的 */}
            {!isExistingUser && (
                <div className={s.footerRow}>
                    <Button type="link" onClick={() => onGo('ehr')}>
                        Already have patients? Explore the free EHR
                    </Button>
                    <span className={s.footerNote}>You can return to this later.</span>
                </div>
            )}
        </>
    );
}
